import { Handle, Position } from '@xyflow/react'
import { rigBoardSource } from '../../ogs/rig/rigBoardSource.js'
import { prettyRigLabel } from '../../lib/rigBoard.js'

/**
 * Rig chain nodes (ENG-946) — the blueprint's way of driving a splat face.
 *
 * Four small nodes in one file because they are one idea: each transforms the
 * POSE beneath it, and they are only meaningful as a chain. The unit is a whole
 * control configuration, not a parameter — which is why there is no "168 pins"
 * node here and never should be.
 *
 *   Rig Pose      a configuration (source of the chain)
 *   Rig Macro     one scalar -> a whole subset (the bundle's own macros)
 *   Rig Control   ONE control, addressed by name
 *   Rig Blend     crossfade two chains
 *
 * Resolution lives in lib/rigChain.js; these are just the editor faces.
 */

export const RIG_CHAIN_COLOR = '#e0a030'

const shell = (selected, active) => ({
  background: selected ? '#20160f' : '#181009',
  border: `2px solid ${active ? '#e0a030' : selected ? '#c08020' : '#504018'}`,
  borderRadius: 6,
  padding: '9px 12px',
  minWidth: 190,
  color: '#ddd',
  fontFamily: 'var(--m-font)',
  fontSize: 12,
})

const title = { color: '#d0a040', fontWeight: 'bold', fontSize: 11, letterSpacing: 0.5 }
const sub = { color: '#8a7040', fontSize: 10, marginTop: 6 }
const field = {
  width: '100%', boxSizing: 'border-box', marginTop: 4,
  background: '#0f0a04', border: '1px solid #504018', color: '#e0c080',
  padding: '2px 4px', borderRadius: 3, fontSize: 10, fontFamily: 'var(--m-mono)', outline: 'none',
}
const dot = (active) => ({ background: active ? '#e0a030' : '#504018', width: 10, height: 10 })


/**
 * A value row for a node. Deliberately a SLIDER, not `input[type=number]`:
 * the native spinner auto-repeats while held and stops on mouseup, and inside
 * a node that re-renders on the editor's debug poll that mouseup can be lost —
 * which reads as "the value ran away on its own". A slider has no repeat, and
 * it is the right shape for a 0..1 control anyway.
 *
 * `nodrag` is ReactFlow's opt-out: without it, dragging the handle drags the
 * whole node instead of moving the value.
 */
function ValueRow({ label, value, min = 0, max = 1, step = 0.01, testId, onChange, pinId, wiredHint }) {
  const span = max - min
  const fill = span > 0 ? `${Math.max(0, Math.min(1, (value - min) / span)) * 100}%` : '0%'
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 5, position: 'relative' }}>
      {/* Wirable data pin, same green as Blend's alpha-in: a wired value wins
          over the variable field, which wins over this slider. */}
      {pinId && (
        <Handle
          type="target"
          position={Position.Left}
          id={pinId}
          style={{ background: '#4ecf7e', width: 8, height: 8, left: -18, top: '50%', transform: 'translateY(-50%)' }}
        />
      )}
      <span style={{ flex: 'none', color: pinId ? '#4ecf7e' : '#8a7040', fontSize: 10 }}>
        {label}{wiredHint ? ' (var)' : ''}
      </span>
      <input
        type="range"
        className="aos-slider nodrag"
        min={min} max={max} step={step}
        value={value}
        data-testid={testId}
        onChange={e => onChange(parseFloat(e.target.value))}
        onMouseDown={e => e.stopPropagation()}
        style={{ flex: 1, minWidth: 0, '--aos-slider-fill': fill }}
      />
      <span style={{ flex: 'none', width: 30, textAlign: 'right', color: '#e0c080', fontSize: 10 }}>
        {Number.isFinite(value) ? value.toFixed(2) : '0.00'}
      </span>
    </div>
  )
}

function Header({ icon, label }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
      <span style={{ fontSize: 14 }}>{icon}</span>
      <span style={title}>{label}</span>
    </div>
  )
}

/** Source of a chain: one whole configuration. */
export function RigPoseNode({ data, selected }) {
  const saved = Array.isArray(data.pose) && data.pose.length > 0
  const poses = rigBoardSource.numPoses || 0
  return (
    <div style={shell(selected, true)}>
      <Header icon="🗿" label="RIG POSE" />
      <div style={sub}>{saved ? 'saved pose (in this node)' : `trained pose${poses ? ` · 0–${poses - 1}` : ''}`}</div>
      {!saved && poses > 1 && (
        <ValueRow
          label="pose" min={0} max={poses - 1} step={1}
          value={data.poseIndex ?? 0}
          testId="rignode-pose-index"
          onChange={v => data.onChange?.('poseIndex', Math.round(v))}
        />
      )}
      <Handle type="source" position={Position.Right} id="anim" style={dot(true)} />
    </div>
  )
}

/** One scalar drives a whole subset — the bundle declares which. */
export function RigMacroNode({ data, selected }) {
  const available = rigBoardSource.macros ? Object.keys(rigBoardSource.macros) : []
  const hasMouth = available.some(k => k.includes('mouth'))
  const hasEyes = true // eye refs are derived, not declared
  return (
    <div style={shell(selected, true)}>
      <Header icon="🎚️" label="RIG MACRO" />
      <div style={sub}>one value → a whole configuration</div>
      <select
        value={data.macro ?? 'mouth'}
        onChange={e => data.onChange?.('macro', e.target.value)}
        onMouseDown={e => e.stopPropagation()}
        className="nodrag"
        style={field}
      >
        {hasMouth && <option value="mouth">mouth open</option>}
        {hasEyes && <option value="eyes">eyes close</option>}
      </select>
      <input
        type="text"
        placeholder="alphaVariable (optional)"
        value={data.alphaVariable ?? ''}
        onChange={e => data.onChange?.('alphaVariable', e.target.value)}
        onMouseDown={e => e.stopPropagation()}
        className="nodrag"
        style={field}
      />
      {!data.alphaVariable && (
        <ValueRow
          label="alpha" value={data.alpha ?? 0} testId="rignode-macro-alpha"
          pinId="alpha-in" wiredHint={!!data.alphaVariable}
          onChange={v => data.onChange?.('alpha', v)}
        />
      )}
      <Handle type="target" position={Position.Left} id="anim" style={dot(true)} />
      <Handle type="source" position={Position.Right} id="anim" style={dot(true)} />
    </div>
  )
}

/** ONE control, addressed by name so a reordered bundle can't repoint it. */
export function RigControlNode({ data, selected }) {
  const names = rigBoardSource.names ?? []
  return (
    <div style={shell(selected, true)}>
      <Header icon="◎" label="RIG CONTROL" />
      <div style={sub}>{names.length ? `${names.length} controls` : 'no bundle loaded'}</div>
      <select
        value={data.control ?? ''}
        onChange={e => data.onChange?.('control', e.target.value)}
        onMouseDown={e => e.stopPropagation()}
        className="nodrag"
        style={field}
      >
        <option value="">— pick a control —</option>
        {names.map(n => <option key={n} value={n}>{prettyRigLabel(n)}</option>)}
      </select>
      <input
        type="text"
        placeholder="valueVariable (optional)"
        value={data.valueVariable ?? ''}
        onChange={e => data.onChange?.('valueVariable', e.target.value)}
        onMouseDown={e => e.stopPropagation()}
        className="nodrag"
        style={field}
      />
      {!data.valueVariable && (
        <ValueRow
          label="value"
          min={rigBoardSource.range?.[0] ?? 0}
          max={rigBoardSource.range?.[1] ?? 1}
          value={data.value ?? 0}
          testId="rignode-control-value"
          pinId="value-in" wiredHint={!!data.valueVariable}
          onChange={v => data.onChange?.('value', v)}
        />
      )}
      <Handle type="target" position={Position.Left} id="anim" style={dot(true)} />
      <Handle type="source" position={Position.Right} id="anim" style={dot(true)} />
    </div>
  )
}

/** Crossfade two chains — the pose equivalent of the clip Blend. */
export function RigBlendNode({ data, selected }) {
  return (
    <div style={shell(selected, true)}>
      <Header icon="⇄" label="RIG BLEND" />
      <div style={sub}>A → B by alpha</div>
      <input
        type="text"
        placeholder="alphaVariable (optional)"
        value={data.alphaVariable ?? ''}
        onChange={e => data.onChange?.('alphaVariable', e.target.value)}
        onMouseDown={e => e.stopPropagation()}
        className="nodrag"
        style={field}
      />
      {!data.alphaVariable && (
        <ValueRow
          label="alpha" value={data.alpha ?? 0.5} testId="rignode-blend-alpha"
          pinId="alpha-in" wiredHint={!!data.alphaVariable}
          onChange={v => data.onChange?.('alpha', v)}
        />
      )}
      <Handle type="target" position={Position.Left} id="a" style={{ ...dot(true), top: '38%' }} />
      <Handle type="target" position={Position.Left} id="b" style={{ ...dot(true), top: '68%' }} />
      <Handle type="source" position={Position.Right} id="anim" style={dot(true)} />
    </div>
  )
}
